import type { NpcHullTier, StarSystem } from "./types";

// Space Map scanner-tier gating (16.46). Section 6's own rule: "a ship's
// scanner tier determines which systems it can perceive/enter at all... a
// jump gate's tier equals the higher danger tier of the two systems it
// connects, gating visibility by scanner tier." No scanner-tier stat
// exists anywhere in the data model (spaceMapScreen.ts's own long-flagged
// gap) -- scanner tier = hull tier (Don's direction), the one stepped
// 1-4 progression that already exists on every ship, rather than
// inventing a separate stat/module.
export function scannerTierFromHullTier(hullTier: NpcHullTier): number {
  return { T1: 1, T2: 2, T3: 3, T4: 4 }[hullTier];
}

// A jump gate's tier (Section 6) -- the higher danger tier of its two
// endpoints.
export function gateTier(a: StarSystem, b: StarSystem): number {
  return Math.max(a.dangerTier, b.dangerTier);
}

// The 10 systems Section 6 locks as `hiddenUntilScanned` (not a real flag
// anywhere in systems.json -- see spaceMapScreen.ts's own long-standing
// note that no such field exists in the data). Hardcoded here since
// there's nowhere else for this locked list to live.
export const HIDDEN_SYSTEM_IDS: readonly string[] = [
  "S034", // Thornehome -- Crimson Concord, frontier/rim
  "S032", // Redjunction -- Crimson Concord, closer-in
  "S046", // Emberbelt -- Azure Compact, frontier/rim
  "S048", // Redhome -- Azure Compact, closer-in
  "S073", // Galefall -- Solthane Accord, frontier/rim
  "S066", // Gullethollow -- Solthane Accord, closer-in
  "S086", // Bluehold -- Underforge pirate pocket
  "S092", // Ferspire -- Ambercrest pirate pocket
  "S097", // Nighthollow -- Caldcradle pirate pocket
  "S106" // Frostvale -- Crescend pirate pocket
];

// Which of the 10 hidden systems border `systemId` -- i.e. which ones a
// scan attempt made while present at `systemId` could detect. The galaxy
// graph is fully bidirectional (verified against all 107 systems), so a
// hidden system's own `connections` list is exactly its real neighbors.
export function hiddenNeighborsOf(systemId: string, systems: StarSystem[]): string[] {
  const byId = new Map(systems.map((s) => [s.id, s]));
  return HIDDEN_SYSTEM_IDS.filter((hiddenId) => {
    const hidden = byId.get(hiddenId);
    return !!hidden && hidden.connections.includes(systemId);
  });
}

export type SystemVisibility = "hidden" | "signal" | "unscanned" | "scanned";

// Section 6's three-state model, extended with "signal" for a detected-
// but-unvisited hidden system (a 4th real state, distinct from ordinary
// "visible, unscanned" -- it shows no stats and only the one edge it was
// detected across, never its own further connections, since those
// genuinely aren't known yet).
//
// Ordinary systems (everything not in HIDDEN_SYSTEM_IDS and not yet
// visited) are reached by flood-filling outward from the always-known
// roots (the 6 Safe Zone systems, dangerTier 0, "well regulated and
// mapped out" from game start -- and every already-visited system, since
// visiting is itself how ordinary tier-gated visibility grows) across
// only those gates whose own tier is <= the captain's real scanner tier.
// A hidden system never enters that flood-fill at all, at any scanner
// tier -- Section 6: "these 10 are never revealed by scanner tier, at any
// level" -- it can only ever reach "signal" via hiddenNeighborsOf's
// detection roll, or "scanned" by being visited outright.
export function computeVisibility(
  systems: StarSystem[],
  visitedIds: string[],
  detectedHiddenIds: string[],
  scannerTier: number
): Map<string, SystemVisibility> {
  const byId = new Map(systems.map((s) => [s.id, s]));
  const visited = new Set(visitedIds);
  const hidden = new Set(HIDDEN_SYSTEM_IDS);
  const result = new Map<string, SystemVisibility>();

  // Roots are always "scanned": either dangerTier 0 (locked-in from game
  // start) or actually visited in person -- both mean full stats are
  // already known, not just position/connections. A visited hidden
  // system is a root too (doc: visiting "converts immediately to a
  // normal, fully-known system") -- `isRoot` alone already captures that,
  // since none of the 10 have dangerTier 0, so only a visited one ever
  // qualifies.
  const isRoot = (s: StarSystem) => s.dangerTier === 0 || visited.has(s.id);
  const queue: string[] = systems.filter(isRoot).map((s) => s.id);
  queue.forEach((id) => result.set(id, "scanned"));

  let i = 0;
  while (i < queue.length) {
    const current = byId.get(queue[i++])!;
    current.connections.forEach((nextId) => {
      if (result.has(nextId)) return; // already resolved (a root, or already queued this pass)
      const next = byId.get(nextId);
      if (!next) return;
      // A not-yet-visited hidden system never enters via ordinary
      // tier-gated flood-fill -- only hiddenNeighborsOf's detection roll
      // ("signal") or an actual visit ("scanned", handled above as a
      // root) ever reveal it.
      if (hidden.has(nextId)) return;
      if (gateTier(current, next) > scannerTier) return; // gate too high-tier to perceive yet
      result.set(nextId, "unscanned");
      queue.push(nextId);
    });
  }

  // Hidden systems: visited outright overrides everything else (doc:
  // "once the captain actually jumps through, it converts immediately to
  // a normal, fully-known system"); otherwise "signal" if detected,
  // otherwise fully hidden regardless of scanner tier.
  HIDDEN_SYSTEM_IDS.forEach((id) => {
    if (visited.has(id)) {
      result.set(id, "scanned");
    } else if (detectedHiddenIds.includes(id)) {
      result.set(id, "signal");
    } else {
      result.set(id, "hidden");
    }
  });

  // Anything never reached by the flood-fill (and not one of the 10) is
  // simply hidden -- too far past the captain's current scanner tier.
  systems.forEach((s) => {
    if (!result.has(s.id)) result.set(s.id, "hidden");
  });

  return result;
}
